import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Skeleton } from "primereact/skeleton";

const GeneralInformationForm = ({
  generaldata,
  handleChange,
  isLoading = false,
  isEdit,
}) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: generaldata,
  });

  useEffect(() => {
    const safeData = generaldata || {};
    reset(safeData);
  }, [generaldata, reset]);

  return (
    <>
      <div className="formgrid grid" data-testid="general-info-form">
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">
            Trading name <span className="required-icon">*</span>
          </label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="trading_name"
              control={control}
              rules={{
                required: "Trading name is required.",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.trading_name || ""}
                  onChange={(e) => {
                    handleChange("trading_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter trading name"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.trading_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.trading_name && (
            <small className="p-error">{errors.trading_name.message}</small>
          )}
        </div>

        <div className="field col-12 md:col-6 sm:col-6">
          <label className="contact-label">
            Start date <span className="required-icon">*</span>
          </label>
          <div className="calendar-container">
            {isLoading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <Controller
                name="start_date"
                control={control}
                // rules={{
                //   required: "Start date is required",
                // }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Calendar
                      {...field}
                      value={
                        generaldata?.start_date
                          ? new Date(generaldata.start_date)
                          : field.value
                      }
                      onChange={(e) => {
                        const isoDate = e.value ? e.value.toISOString() : "";
                        field.onChange(isoDate);
                        handleChange("start_date", isoDate);
                        if (generaldata?.end_date) {
                          handleChange("end_date", null);
                        }
                      }}
                      placeholder="Select date"
                      className="w-full"
                      disabled={!isEdit}
                      data-testid="lsp-select-date"
                    />
                    <span
                      className="pi pi-calendar calendar-icon"
                      data-testid="calendar-icon"
                    ></span>
                    {!generaldata?.start_date && error && (
                      <small className="p-error" data-testid="start-date-error">
                        {error.message}
                      </small>
                    )}
                  </>
                )}
              />
            )}
          </div>
        </div>

        <div className="field col-12 md:col-6 sm:col-6">
          <label className="contact-label">End date (If applicable)</label>
          <div className="calendar-container">
            {isLoading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <Controller
                name="end_date"
                control={control}
                render={({ field }) => (
                  <>
                    <Calendar
                      {...field}
                      value={
                        generaldata?.end_date
                          ? new Date(generaldata.end_date)
                          : field.value
                      }
                      onChange={(e) => {
                        const isoDate = e.value ? e.value.toISOString() : "";

                        handleChange("end_date", isoDate);
                        field.onChange(isoDate);
                      }}
                      minDate={
                        generaldata?.start_date
                          ? new Date(
                              new Date(generaldata?.start_date).setDate(
                                new Date(generaldata?.start_date).getDate() + 1,
                              ),
                            )
                          : null
                      }
                      disabled={!generaldata?.start_date || !isEdit}
                      placeholder="Select date"
                      className="w-full"
                      data-testid="lsp-end-date"
                    />
                    <span className="pi pi-calendar calendar-icon"></span>
                  </>
                )}
              />
            )}
          </div>
        </div>
        <div className="field col-12 md:col-6 sm:col-12">
          <div className="formgrid grid">
            <div className="col-12 md:col-12 sm:col-12">
              <label className="input-label">
                Local Team Phone number <span className="required-icon">*</span>
              </label>
            </div>

            <div className="pt-2 col-12 md:col-4 sm:col-4 ">
              {isLoading ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <Controller
                  name="phone_code"
                  control={control}
                  rules={{
                    required: "Country code is required.",
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "Country code must contain only digits.",
                    },
                  }}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      value={generaldata?.phone_code || ""}
                      onChange={(e) => {
                        handleChange("phone_code", e.target.value);
                        field.onChange(e);
                      }}
                      type="text"
                      placeholder="Country Code"
                      className={`input-field appearance-none outline-none w-full ${
                        errors.phone_code ? "p-invalid" : ""
                      }`}
                      disabled={!isEdit}
                      data-testid="phone-code"
                    />
                  )}
                />
              )}

              {errors.phone_code && (
                <small className="p-error">{errors.phone_code.message}</small>
              )}
            </div>

            <div className="pt-2 col-12 md:col-8 sm:col-8">
              {isLoading ? (
                <Skeleton
                  data-testid="skeleton-loader"
                  className="p-4"
                ></Skeleton>
              ) : (
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Phone number is required.",
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "Phone number must contain only digits.",
                    },
                  }}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      value={generaldata?.phone || ""}
                      onChange={(e) => {
                        handleChange("phone", e.target.value);
                        field.onChange(e);
                      }}
                      type="text"
                      placeholder="Enter Phone Number"
                      className={`input-field appearance-none outline-none w-full ${
                        errors.phone ? "p-invalid" : ""
                      }`}
                      disabled={!isEdit}
                      data-testid="lsp-phone"
                    />
                  )}
                />
              )}

              {errors.phone && (
                <small className="p-error">{errors.phone.message}</small>
              )}
            </div>
          </div>
        </div>

        <div className="field col-12 md:col-6 sm:col-12">
          <label className="input-label">
            Local Team Email <span className="required-icon">*</span>
          </label>

          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.email || ""}
                  onChange={(e) => {
                    handleChange("email", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter email address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.email ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="lsp-email"
                />
              )}
            />
          )}

          {errors.email && (
            <small className="p-error">{errors.email.message}</small>
          )}
        </div>

        <div className="field col-12 md:col-6 sm:col-6">
          <label className="input-label">
            Account Manager <span className="required-icon">*</span>
          </label>

          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="hsp_manager_name"
              control={control}
              rules={{
                required: "Account manager name is required.",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.hsp_manager_name || ""}
                  onChange={(e) => {
                    handleChange("hsp_manager_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter manager name"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.hsp_manager_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="hsp-manger-name"
                />
              )}
            />
          )}
          {errors.hsp_manager_name && (
            <small className="p-error">{errors.hsp_manager_name.message}</small>
          )}
        </div>

        <div className="field col-12 md:col-6 sm:col-6">
          <label className="input-label">
            Account Manager Email<span className="required-icon">*</span>
          </label>

          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="hsp_manager_email"
              control={control}
              rules={{
                required: "Account manager email is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.hsp_manager_email || ""}
                  onChange={(e) => {
                    handleChange("hsp_manager_email", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter manager email"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.hsp_manager_email ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="hsp-manger-email"
                />
              )}
            />
          )}

          {errors.hsp_manager_email && (
            <small className="p-error">
              {errors.hsp_manager_email.message}
            </small>
          )}
        </div>

        <div className=" col-12 md:col-12 sm:col-12">
          <p>
            <b>Point of Contact</b>
          </p>
        </div>

        <div className="field col-12 md:col-6 sm:col-6">
          <label className="input-label">
            Full Name<span className="required-icon">*</span>
          </label>

          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="account_manager_name"
              control={control}
              rules={{
                required: "Full Name is required.",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.account_manager_name || ""}
                  onChange={(e) => {
                    handleChange("account_manager_name", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter manager name"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.account_manager_name ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-name"
                />
              )}
            />
          )}
          {errors.account_manager_name && (
            <small className="p-error">
              {errors.account_manager_name.message}
            </small>
          )}
        </div>

        <div className="field col-12 md:col-6 sm:col-6">
          <label className="input-label">
            Email<span className="required-icon">*</span>
          </label>

          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="account_manager_email"
              control={control}
              rules={{
                required: "Email is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.account_manager_email || ""}
                  onChange={(e) => {
                    handleChange("account_manager_email", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter email address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.account_manager_email ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-email"
                />
              )}
            />
          )}

          {errors.account_manager_email && (
            <small className="p-error">
              {errors.account_manager_email.message}
            </small>
          )}
        </div>

        <div className="col-12 md:col-12 sm:col-12">
          <label className="input-label">
            Phone number <span className="required-icon">*</span>
          </label>
        </div>
        <div className="pt-2 col-12 md:col-3 sm:col-4 ">
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="account_manager_phone_code"
              control={control}
              rules={{
                required: "Country code is required.",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Country code must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.account_manager_phone_code || ""}
                  onChange={(e) => {
                    handleChange("account_manager_phone_code", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Country Code"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.account_manager_phone_code ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-phone-code"
                />
              )}
            />
          )}

          {errors.account_manager_phone_code && (
            <small className="p-error">
              {errors.account_manager_phone_code.message}
            </small>
          )}
        </div>

        <div className="pt-2  col-12 md:col-9 sm:col-8">
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="account_manager_phone"
              control={control}
              defaultValue=""
              rules={{
                required: "Phone number is required.",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone number must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={generaldata?.account_manager_phone || ""}
                  onChange={(e) => {
                    handleChange("account_manager_phone", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter Phone Number"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.account_manager_phone ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="manager-phone"
                />
              )}
            />
          )}

          {errors.account_manager_phone && (
            <small className="p-error">
              {errors.account_manager_phone.message}
            </small>
          )}
        </div>
      </div>
    </>
  );
};
GeneralInformationForm.propTypes = {
  generaldata: PropTypes.object,
  handleChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
};
export default GeneralInformationForm;
